import { Container, Flex, Text, Heading, Grid } from '@radix-ui/themes';
import {
  CheckIcon,
  CircleBackslashIcon,
  PlusIcon,
  MinusIcon,
} from '@radix-ui/react-icons';
import * as Accordion from '@radix-ui/react-accordion';
import { cn } from '@trashlab/sitesapp/src/@/lib/utils';
import {
  IconAllowed,
  IconNotAllowed,
} from '@trashlab/sitesapp/src/organizations/best-deal/template/components/icons/icons';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/best-deal/lib/utils/get-variant-value';
import { useMemo } from 'react';
import { Card } from '@radix-ui/themes';

type VariantValue = {
  value: string;
  orderIndex?: number;
};

const MaterialItem = ({
  icon,
  text,
  description,
}: {
  icon: React.ReactNode;
  text: string;
  description: string;
}) => (
  <Accordion.Item value={text} className="bg-white rounded-md">
    <Accordion.Trigger
      className="h-[80px] items-center text-left group flex w-full justify-between gap-5 px-6 
                text-lg font-semibold leading-none rounded-md bg-white border-0
                shadow-[0px_7px_26px_rgba(0,0,0,0.08)] 
                hover:bg-[var(--background-color-light)] 
                data-[state=open]:bg-[var(--background-color-light)]"
    >
      <Flex align="center" gap="5" className="flex-1">
        <span className="flex-shrink-0">{icon}</span>
        <Text size="3" weight="medium">
          {text}
        </Text>
      </Flex>
      <span className="shrink-0">
        <PlusIcon className="w-6 h-6 transition-all duration-200 ease-out transform group-data-[state=open]:scale-0 group-data-[state=open]:opacity-0 group-data-[state=open]:hidden text-[var(--primary-color,#3463F8)] group-hover:text-[var(--primary-color,#3463F8)]" />
        <MinusIcon className="w-6 h-6 transition-all duration-200 ease-out transform scale-0 opacity-0 hidden group-data-[state=open]:scale-100 group-data-[state=open]:opacity-100 group-data-[state=open]:block text-[var(--primary-color,#3463F8)]" />
      </span>
    </Accordion.Trigger>

    <Accordion.Content
      className={cn(
        'overflow-hidden will-change-[height] transition-[height] duration-200 ease-out border-0',
        'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
        'px-6 py-6 mt-0 bg-white rounded-md shadow-[0px_7px_26px_rgba(0,0,0,0.08)]',
      )}
    >
      <div className="animate-fade-in">
        <Text size="2" className="leading-7">
          {description}
        </Text>
      </div>
    </Accordion.Content>
  </Accordion.Item>
);

export const MaterialGuide = ({
  withBackground = false,
}: {
  withBackground?: boolean;
}) => {
  const defaultAllowedItems = [
    {
      text: 'General household items',
      description:
        'Household items like housewares, personal items, clothing, plastics, and paper are welcome. Furniture, including mattresses, box springs, chairs, couches, and sofa beds can be disposed of in our dumpsters.',
    },
    {
      text: 'Household chemicals and pesticides',
      description:
        'We accept a variety of household chemicals and pesticides, provided that they are in their original, securely sealed containers.',
    },
    {
      text: 'Home electronics and appliances',
      description:
        'We accept a range of electronics, including TVs, computers, and printers. Appliances like microwaves, stoves, dishwashers, washers, dryers, ovens, AC Units, swamp coolers, freezers, and refrigerators.',
    },
    {
      text: 'Paint and chemicals',
      description:
        'Both empty and full paint cans are accepted, so long as they are securely sealed to prevent leaks.',
    },
  ];

  const defaultNotAllowedItems = [
    {
      text: 'Batteries',
      description:
        'Absolutely no batteries are allowed, including car batteries or lithium batteries of any kind.',
    },
    {
      text: 'Biohazardous materials',
      description:
        'Items such as fecal matter, deceased animals, medical waste, and any material contaminated with potentially infectious substances cannot be disposed of in our dumpsters.',
    },
    {
      text: 'Food waste',
      description:
        'Our service does not accept food waste, including but not limited to leftovers, spoiled food, fruit and vegetable peels, coffee grounds, eggshells, bones, and other kitchen scraps. Please dump all food waste into your regular city provided garbage cans.',
    },
    {
      text: 'Weapons',
      description:
        'Please do not dispose of any weapons, firearms, ammunition, or related items in our dumpsters. These materials are potentially dangerous and require special disposal procedures that are beyond the scope of our standard services.',
    },
  ];

  const allowedNames = useVariantValue<VariantValue[]>(
    'ALLOWED_MATERIAL_NAME',
    [],
  );
  const allowedDescriptions = useVariantValue<VariantValue[]>(
    'ALLOWED_MATERIAL_DESCRIPTION',
    [],
  );
  const notAllowedNames = useVariantValue<VariantValue[]>(
    'NOT_ALLOWED_MATERIAL_NAME',
    [],
  );
  const notAllowedDescriptions = useVariantValue<VariantValue[]>(
    'NOT_ALLOWED_MATERIAL_DESCRIPTION',
    [],
  );

  const materialGuideTitle = useVariantValue<string>(
    'MATERIAL_GUIDE_TITLE',
    'Allowed VS not allowed dumpster material guide',
  );

  const allowedItems = useMemo(() => {
    if (!allowedNames.length && !allowedDescriptions.length) {
      return defaultAllowedItems;
    }

    const orderIndexes = [
      ...new Set([
        ...allowedNames.map((n) => n.orderIndex ?? 0),
        ...allowedDescriptions.map((d) => d.orderIndex ?? 0),
      ]),
    ].sort((a, b) => a - b);

    return orderIndexes
      .map((index) => {
        const text = allowedNames.find((n) => n.orderIndex === index)?.value;
        const description = allowedDescriptions.find(
          (d) => d.orderIndex === index,
        )?.value;
        const defaultItem = defaultAllowedItems[index];

        return {
          text: text ?? defaultItem?.text ?? '',
          description: description ?? defaultItem?.description ?? '',
        };
      })
      .filter((item) => item.text && item.description);
  }, [allowedNames, allowedDescriptions]);

  const notAllowedItems = useMemo(() => {
    if (!notAllowedNames.length && !notAllowedDescriptions.length) {
      return defaultNotAllowedItems;
    }

    const orderIndexes = [
      ...new Set([
        ...notAllowedNames.map((n) => n.orderIndex ?? 0),
        ...notAllowedDescriptions.map((d) => d.orderIndex ?? 0),
      ]),
    ].sort((a, b) => a - b);

    return orderIndexes
      .map((index) => {
        const text = notAllowedNames.find((n) => n.orderIndex === index)?.value;
        const description = notAllowedDescriptions.find(
          (d) => d.orderIndex === index,
        )?.value;
        const defaultItem = defaultNotAllowedItems[index];

        return {
          text: text ?? defaultItem?.text ?? '',
          description: description ?? defaultItem?.description ?? '',
        };
      })
      .filter((item) => item.text && item.description);
  }, [notAllowedNames, notAllowedDescriptions]);

  return (
    <Container
      size="4"
      p="8"
      className={
        withBackground
          ? `relative min-h-[200px] overflow-hidden before:content-[''] before:absolute before:inset-0 before:bg-[url('/default/rental-steps-bg.svg')] before:bg-cover before:bg-center before:bg-no-repeat before:opacity-100 before:-z-10 after:content-[''] after:absolute after:inset-0 after:opacity-90 after:-z-10`
          : ''
      }
    >
      <Flex direction="column" gap="6">
        <Heading as="h2" size="8" data-variable="MATERIAL_GUIDE_TITLE">
          {materialGuideTitle}
        </Heading>

        <Grid columns={{ initial: '1', sm: '2' }} gap="4">
          <Flex direction="column" gap="4">
            <Flex gap="2" align="center">
              <CheckIcon className="w-8 h-8 text-green-500" />
              <Heading as="h3" size="6" className="text-green-500">
                Allowed items
              </Heading>
            </Flex>
            <Accordion.Root type="single" collapsible className="space-y-4">
              {allowedItems.map((item, index) => (
                <Card key={index} className="p-4">
                  <Flex align="center" gap="3">
                    <IconAllowed className="text-green-600 w-5 h-5 flex-shrink-0" />
                    <Flex direction="column">
                      <Text
                        size="3"
                        weight="bold"
                        data-variable="ALLOWED_MATERIAL_NAME"
                        data-order-index={index}
                      >
                        {item.text}
                      </Text>
                      {item.description && (
                        <Text
                          size="2"
                          className="text-gray-600"
                          data-variable="ALLOWED_MATERIAL_DESCRIPTION"
                          data-order-index={index}
                        >
                          {item.description}
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                </Card>
              ))}
            </Accordion.Root>
          </Flex>

          <Flex direction="column" gap="4">
            <Flex gap="2" align="center">
              <CircleBackslashIcon className="w-8 h-8 text-red-500" />
              <Heading as="h3" size="6" className="text-red-500">
                Not allowed items
              </Heading>
            </Flex>
            <Accordion.Root type="single" collapsible className="space-y-4">
              {notAllowedItems.map((item, index) => (
                <Card key={index} className="p-4">
                  <Flex align="center" gap="3">
                    <IconNotAllowed className="text-red-600 w-5 h-5 flex-shrink-0" />
                    <Flex direction="column">
                      <Text
                        size="3"
                        weight="bold"
                        data-variable="NOT_ALLOWED_MATERIAL_NAME"
                        data-order-index={index}
                      >
                        {item.text}
                      </Text>
                      {item.description && (
                        <Text
                          size="2"
                          className="text-gray-600"
                          data-variable="NOT_ALLOWED_MATERIAL_DESCRIPTION"
                          data-order-index={index}
                        >
                          {item.description}
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                </Card>
              ))}
            </Accordion.Root>
          </Flex>
        </Grid>
      </Flex>
    </Container>
  );
};

export default MaterialGuide;
